import { format as formatNumber } from 'd3-format'
import { TriangleCell, TriangleCellCoordinate, TriangleGradingMethod } from './types'

export interface TriangleGrid {
  originDates: string[]
  developmentPeriods: string[]
  cellByKey: Map<string, TriangleCell>
}

export function cellKey(originDate: string, developmentPeriod: string): string {
  return `${originDate}\0${developmentPeriod}`
}

/** How a cell should be rendered with respect to server vs pending exclusion state. */
export type TriangleCellExclusionDisplay =
  | 'unchanged-included'
  | 'unchanged-excluded'
  | 'pending-exclude'
  | 'pending-include'

export function getEffectiveExcluded(
  cell: TriangleCell,
  exclusionOverrides: Map<string, boolean>
): boolean {
  const key = cellKey(cell.originDate, cell.developmentPeriod)
  if (exclusionOverrides.has(key)) return exclusionOverrides.get(key)!
  return cell.excluded
}

export function getCellExclusionDisplay(
  cell: TriangleCell,
  exclusionOverrides: Map<string, boolean>
): TriangleCellExclusionDisplay {
  const key = cellKey(cell.originDate, cell.developmentPeriod)
  const override = exclusionOverrides.get(key)
  if (override === undefined || override === cell.excluded) {
    return cell.excluded ? 'unchanged-excluded' : 'unchanged-included'
  }
  return override ? 'pending-exclude' : 'pending-include'
}

export function hasExclusionOverrides(exclusionOverrides: Map<string, boolean>, cellByKey: Map<string, TriangleCell>): boolean {
  for (const [key, excluded] of exclusionOverrides) {
    const cell = cellByKey.get(key)
    if (cell && cell.excluded !== excluded) return true
  }
  return false
}

export function buildTriangleGrid(cells: TriangleCell[]): TriangleGrid {
  const originDates = [...new Set(cells.map((c) => c.originDate))].sort(compareOriginDates)
  const developmentPeriods = [...new Set(cells.map((c) => c.developmentPeriod))].sort(compareDevelopmentPeriods)

  const cellByKey = new Map<string, TriangleCell>()
  for (const cell of cells) {
    cellByKey.set(cellKey(cell.originDate, cell.developmentPeriod), cell)
  }

  return { originDates, developmentPeriods, cellByKey }
}

function compareOriginDates(a: string, b: string): number {
  const dateA = Date.parse(a)
  const dateB = Date.parse(b)
  if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) return dateA - dateB
  return a.localeCompare(b, undefined, { numeric: true })
}

/** Sort development periods by their leading numeric value (handles "12", "12-24", "3-6"). */
function compareDevelopmentPeriods(a: string, b: string): number {
  return developmentPeriodSortKey(a) - developmentPeriodSortKey(b)
}

function developmentPeriodSortKey(period: string): number {
  const match = period.match(/-?\d+/)
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER
}

export function formatTriangleValue(value: number, valueFormat?: string): string {
  if (valueFormat) {
    try {
      return formatNumber(valueFormat)(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

export function getCellGradingBackground(
  gradingMethod: TriangleGradingMethod | null | undefined,
  gradingValue: number | null | undefined,
  isDarkMode = false
): string | undefined {
  if (!gradingMethod || gradingValue == null) return undefined

  const t = Math.min(1, Math.max(0, gradingValue))
  const saturation = isDarkMode ? 45 : 65

  if (gradingMethod === 'overall') {
    const lightness = isDarkMode ? 38 - t * 24 : 92 - t * 52
    return `hsl(210 ${saturation}% ${lightness}%)`
  }

  // column: green (0) → amber (0.5) → red (1)
  const hue = t <= 0.5 ? 142 + (45 - 142) * (t * 2) : 45 + (0 - 45) * ((t - 0.5) * 2)
  const chromaSaturation = isDarkMode ? 50 : 55
  const lightness = isDarkMode ? 36 - Math.abs(t - 0.5) * 18 : 88 - Math.abs(t - 0.5) * 20
  return `hsl(${hue} ${chromaSaturation}% ${lightness}%)`
}

const LEGEND_GRADIENT_STOPS = [0, 0.25, 0.5, 0.75, 1] as const

/** CSS linear-gradient matching {@link getCellGradingBackground} for the legend bar. */
export function getTriangleLegendGradientCss(gradingMethod: TriangleGradingMethod, isDarkMode = false): string {
  const stops = LEGEND_GRADIENT_STOPS.map((t) => {
    const color = getCellGradingBackground(gradingMethod, t, isDarkMode)!
    return `${color} ${t * 100}%`
  })
  return `linear-gradient(to right, ${stops.join(', ')})`
}

export interface TriangleLegendLabels {
  title: string
  startLabel: string
  endLabel: string
  ariaLabel: string
}

export function getTriangleLegendLabels(gradingMethod: TriangleGradingMethod): TriangleLegendLabels {
  if (gradingMethod === 'overall') {
    return {
      title: 'Value intensity',
      startLabel: 'Lower',
      endLabel: 'Higher',
      ariaLabel: 'Colour scale from lower values (light blue) to higher values (dark blue)',
    }
  }
  return {
    title: 'Deviation from column mean',
    startLabel: 'At mean',
    endLabel: 'Far from mean',
    ariaLabel: 'Colour scale from at column mean (green) through amber to far from mean (red)',
  }
}

export function isFullyExcludedAxis(
  axisValues: string[],
  crossAxisValues: string[],
  cellByKey: Map<string, TriangleCell>,
  axis: 'row' | 'column',
  isExcluded: (cell: TriangleCell) => boolean
): Record<string, boolean> {
  const result: Record<string, boolean> = {}

  for (const axisValue of axisValues) {
    let observedCount = 0
    let excludedCount = 0

    for (const crossValue of crossAxisValues) {
      const originDate = axis === 'row' ? axisValue : crossValue
      const developmentPeriod = axis === 'column' ? axisValue : crossValue
      const cell = cellByKey.get(cellKey(originDate, developmentPeriod))
      if (!cell) continue
      observedCount++
      if (isExcluded(cell)) excludedCount++
    }

    result[axisValue] = observedCount > 0 && excludedCount === observedCount
  }

  return result
}

export function buildExclusionSubmitPayload(
  cells: TriangleCell[],
  exclusionOverrides: Map<string, boolean>
): { excludedCells: TriangleCellCoordinate[]; includedCells: TriangleCellCoordinate[] } {
  const excludedCells: TriangleCellCoordinate[] = []
  const includedCells: TriangleCellCoordinate[] = []

  for (const cell of cells) {
    const coordinate: TriangleCellCoordinate = {
      originDate: cell.originDate,
      developmentPeriod: cell.developmentPeriod,
    }

    if (getEffectiveExcluded(cell, exclusionOverrides)) {
      excludedCells.push(coordinate)
    }

    if (getCellExclusionDisplay(cell, exclusionOverrides) === 'pending-include') {
      includedCells.push(coordinate)
    }
  }

  return { excludedCells, includedCells }
}

export function exportTriangleCsv(params: {
  originDates: string[]
  developmentPeriods: string[]
  cellByKey: Map<string, TriangleCell>
  isExcluded: (cell: TriangleCell) => boolean
  valueFormat?: string
}): string {
  const { originDates, developmentPeriods, cellByKey, isExcluded, valueFormat } = params
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`

  const header = ['', ...developmentPeriods.map(escape)].join(',')
  const rows = originDates.map((originDate) => {
    const values = developmentPeriods.map((developmentPeriod) => {
      const cell = cellByKey.get(cellKey(originDate, developmentPeriod))
      if (!cell || isExcluded(cell)) return ''
      return formatTriangleValue(cell.value, valueFormat)
    })
    return [escape(originDate), ...values.map(escape)].join(',')
  })

  return [header, ...rows].join('\n')
}

export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

export function exportFilenamePrefix(exportFileName: string | undefined, title: string | undefined): string {
  return exportFileName?.trim() || title?.trim() || 'triangle'
}
